import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

import EmailIcon from '@atlaskit/icon/core/email';
import PageIcon from '@atlaskit/icon/core/page';
import LabelIcon from '@atlaskit/icon/core/tag';
import LockIcon from '@atlaskit/icon/core/lock-locked';
import PersonIcon from '@atlaskit/icon/core/person';
import CopyIcon from '@atlaskit/icon/core/copy';
import RightArrowIcon from '@atlaskit/icon/core/arrow-right';
import ShieldIcon from '@atlaskit/icon/core/shield';
import DatabaseIcon from '@atlaskit/icon/core/database';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import FolderIcon from '@atlaskit/icon/core/folder-closed';
import CommentIcon from '@atlaskit/icon/core/comment';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';

import { ManualTriggerIcon as SmartButtonIcon } from '@confluence/icons/entry-points/ManualTriggerIcon.ts';

import { JiraLogoIcon } from '../../../../assets/JiraIcon.tsx';
import { RovoLogoIcon } from '../../../../assets/RovoIcon.tsx';
import { TemplateId } from '../../../smart-button-form-data/smartButtonFormDataTypes.ts';

export enum ComponentType {
	TRIGGER = 'TRIGGER',
	ACTION = 'ACTION',
}

type TemplateIconProps = {
	componentType: ComponentType;
	icon: React.ElementType;
	isCustomIcon?: boolean;
	fallbackIcon?: React.ElementType;
};

export type Template = {
	id: string;
	description: MessageDescriptor;
	icons: TemplateIconProps[];
};

export const i18n = defineMessages({
	changePageStatusAndSendEmailDescription: {
		id: 'automation-extensions.smart-button.template-card.change-page-status-and-send-email.description',
		defaultMessage: 'Change the page’s status and send an email',
		description: 'The description of the change page status and send email template.',
	},
	publishNewPageAndAddLabelDescription: {
		id: 'automation-extensions.smart-button.template-card.publish-new-page-and-add-label.description',
		defaultMessage: 'Publish a new page and add label(s)',
		description: 'The description of the publish new page and add labels template.',
	},
	addLabelAndChangePageStatusDescription: {
		id: 'automation-extensions.smart-button.template-card.add-label-and-change-page-status.description',
		defaultMessage: 'Add label(s) and change the page’s status',
		description: 'The description of the add label and change page status template.',
	},
	changePageRestrictionsAndSendEmailDescription: {
		id: 'automation-extensions.smart-button.template-card.change-page-restrictions-and-send-email.description',
		defaultMessage: 'Change the page’s restrictions and send an email',
		description: 'The description of the restrict page and send email template.',
	},
	changePageOwnerAndSendEmailDescription: {
		id: 'automation-extensions.smart-button.template-card.change-page-owner-and-send-email.description',
		defaultMessage: 'Change the page owner and send an email',
		description: 'The description of the change page owner and send email template.',
	},
	copyPageAndChangeOwnerAndStatusAndRestrictionsDescription: {
		id: 'automation-extensions.smart-button.template-card.copy-page-and-change-owner-and-status-and-restrictions.description',
		defaultMessage:
			'Copy the page, change the page owner, the page’s status, and the page’s restrictions',
		description:
			'The description of the copy page, change page owner, change page status, and restrict page template.',
	},
	publishPageAndChangeOwnerAndStatusAndRestrictionsDescription: {
		id: 'automation-extensions.smart-button.template-card.publish-page-and-change-owner-and-status-and-restrictions.description',
		defaultMessage:
			'Publish a new page, change the page owner, the page’s status, and the page’s restrictions',
		description:
			'The description of the publish new page, change page owner, change page status and restrict page template.',
	},
	// New templates
	movePageAndAddLabelDescription: {
		id: 'automation-extensions.smart-button.template-card.move-page-and-add-label.description',
		defaultMessage: 'Move page and add label',
		description: 'The description of the move page and add label template.',
	},
	updateDataClassificationAndChangeRestrictionsAndSendEmailDescription: {
		id: 'automation-extensions.smart-button.template-card.update-data-classification-and-change-restrictions-and-send-email.description',
		defaultMessage: 'Update data classification, change restrictions, and send email',
		description:
			'The description of the update data classification, change restrictions, and send email template.',
	},
	summarizeWithAIAndGenerateAIActionItemsAndSendEmailDescription: {
		id: 'automation-extensions.smart-button.template-card.summarize-with-ai-and-generate-ai-action-items-and-send-email.description',
		defaultMessage: 'Summarize with AI, Generate AI action items, and send email',
		description:
			'The description of the summarize with AI, Generate AI action items, and send email	template.',
	},
	createDatabaseAndSendEmailDescription: {
		id: 'automation-extensions.smart-button.template-card.create-database-and-send-email.description',
		defaultMessage: 'Create database and send email',
		description: 'The description of the create database and send email template.',
	},
	createWhiteboardAndSendEmailDescription: {
		id: 'automation-extensions.smart-button.template-card.create-whiteboard-and-send-email.description',
		defaultMessage: 'Create whiteboard and send email',
		description: 'The description of the create whiteboard and send email template.',
	},
	publishNewPageAddLabelCreateJiraIssueDescription: {
		id: 'automation-extensions.smart-button.template-card.publish-new-page-add-label-create-jira-issue.description',
		defaultMessage: 'Publish a new page, add a label, and create issue in Jira',
		description:
			'Text displayed to the user that describes the trigger and following actions for an automation rule template',
	},
	createFolderAndPublishNewPageDescription: {
		id: 'automation-extensions.smart-button.template-card.create-folder-and-publish-new-page.description',
		defaultMessage: 'Create folder and publish new page',
		description: 'The description of the create folder and publish new page template.',
	},
	invokeAgentAndPublishNewPageDescription: {
		id: 'automation-extensions.smart-button.template-card.invoke-agent-and-publish-new-page.description',
		defaultMessage: 'Use Rovo agent and publish new page',
		description: 'The description of the invoke agent and publish new page template.',
	},
	invokeAgentAndSendEmailDescription: {
		id: 'automation-extensions.smart-button.template-card.invoke-agent-and-send-email.description',
		defaultMessage: 'Use Rovo agent and send email',
		description: 'The description of the invoke agent and send email template.',
	},
	invokeAgentAndAddCommentDescription: {
		id: 'automation-extensions.smart-button.template-card.invoke-agent-and-add-comment.description',
		defaultMessage: 'Use Rovo agent and add comment',
		description: 'The description of the invoke agent and add comment template.',
	},
});

export const templateCardConfigs: Template[] = [
	{
		id: TemplateId.changePageStatusAndSendEmail,
		description: i18n.changePageStatusAndSendEmailDescription,
		icons: [
			{ componentType: ComponentType.TRIGGER, icon: SmartButtonIcon, isCustomIcon: true },
			{ componentType: ComponentType.ACTION, icon: PageIcon },
			{ componentType: ComponentType.ACTION, icon: EmailIcon },
		],
	},
	{
		id: TemplateId.publishNewPageAndAddLabel,
		description: i18n.publishNewPageAndAddLabelDescription,
		icons: [
			{ componentType: ComponentType.TRIGGER, icon: SmartButtonIcon, isCustomIcon: true },
			{ componentType: ComponentType.ACTION, icon: PageIcon },
			{ componentType: ComponentType.ACTION, icon: LabelIcon },
		],
	},
	{
		id: TemplateId.addLabelAndChangePageStatus,
		description: i18n.addLabelAndChangePageStatusDescription,
		icons: [
			{ componentType: ComponentType.TRIGGER, icon: SmartButtonIcon, isCustomIcon: true },
			{ componentType: ComponentType.ACTION, icon: LabelIcon },
			{ componentType: ComponentType.ACTION, icon: PageIcon },
		],
	},
	{
		id: TemplateId.changePageRestrictionsAndSendEmail,
		description: i18n.changePageRestrictionsAndSendEmailDescription,
		icons: [
			{ componentType: ComponentType.TRIGGER, icon: SmartButtonIcon, isCustomIcon: true },
			{ componentType: ComponentType.ACTION, icon: LockIcon },
			{ componentType: ComponentType.ACTION, icon: EmailIcon },
		],
	},
	{
		id: TemplateId.changePageOwnerAndSendEmail,
		description: i18n.changePageOwnerAndSendEmailDescription,
		icons: [
			{ componentType: ComponentType.TRIGGER, icon: SmartButtonIcon, isCustomIcon: true },
			{ componentType: ComponentType.ACTION, icon: PersonIcon },
			{ componentType: ComponentType.ACTION, icon: EmailIcon },
		],
	},
	{
		id: TemplateId.copyPageAndChangeOwnerAndStatusAndRestrictions,
		description: i18n.copyPageAndChangeOwnerAndStatusAndRestrictionsDescription,
		icons: [
			{ componentType: ComponentType.TRIGGER, icon: SmartButtonIcon, isCustomIcon: true },
			{ componentType: ComponentType.ACTION, icon: CopyIcon },
			{ componentType: ComponentType.ACTION, icon: PersonIcon },
			{ componentType: ComponentType.ACTION, icon: PageIcon },
			{ componentType: ComponentType.ACTION, icon: LockIcon },
		],
	},
	{
		id: TemplateId.publishPageAndChangeOwnerAndStatusAndRestrictions,
		description: i18n.publishPageAndChangeOwnerAndStatusAndRestrictionsDescription,
		icons: [
			{ componentType: ComponentType.TRIGGER, icon: SmartButtonIcon, isCustomIcon: true },
			{ componentType: ComponentType.ACTION, icon: PageIcon },
			{ componentType: ComponentType.ACTION, icon: PersonIcon },
			{ componentType: ComponentType.ACTION, icon: PageIcon },
			{ componentType: ComponentType.ACTION, icon: LockIcon },
		],
	},
];

export const AITemplateCardConfig: Template = {
	id: TemplateId.summarizeWithAIAndGenerateAIActionItemsAndSendEmail,
	description: i18n.summarizeWithAIAndGenerateAIActionItemsAndSendEmailDescription,
	icons: [
		{ componentType: ComponentType.TRIGGER, icon: SmartButtonIcon, isCustomIcon: true },
		{
			componentType: ComponentType.ACTION,
			icon: AtlassianIntelligenceIcon,
		},
		{
			componentType: ComponentType.ACTION,
			icon: AtlassianIntelligenceIcon,
		},
		{ componentType: ComponentType.ACTION, icon: EmailIcon },
	],
};

export const newTemplateCardConfigs: Template[] = [
	{
		id: TemplateId.movePageAndAddLabel,
		description: i18n.movePageAndAddLabelDescription,
		icons: [
			{ componentType: ComponentType.TRIGGER, icon: SmartButtonIcon, isCustomIcon: true },
			{
				componentType: ComponentType.ACTION,
				icon: RightArrowIcon,
			},
			{ componentType: ComponentType.ACTION, icon: LabelIcon },
		],
	},
	{
		id: TemplateId.updateDataClassificationAndChangeRestrictionsAndSendEmail,
		description: i18n.updateDataClassificationAndChangeRestrictionsAndSendEmailDescription,
		icons: [
			{ componentType: ComponentType.TRIGGER, icon: SmartButtonIcon, isCustomIcon: true },
			{ componentType: ComponentType.ACTION, icon: ShieldIcon },
			{ componentType: ComponentType.ACTION, icon: LockIcon },
			{ componentType: ComponentType.ACTION, icon: EmailIcon },
		],
	},
	{
		id: TemplateId.createDatabaseAndSendEmail,
		description: i18n.createDatabaseAndSendEmailDescription,
		icons: [
			{ componentType: ComponentType.TRIGGER, icon: SmartButtonIcon, isCustomIcon: true },
			{ componentType: ComponentType.ACTION, icon: DatabaseIcon },
			{ componentType: ComponentType.ACTION, icon: EmailIcon },
		],
	},
	{
		id: TemplateId.createWhiteboardAndSendEmail,
		description: i18n.createWhiteboardAndSendEmailDescription,
		icons: [
			{ componentType: ComponentType.TRIGGER, icon: SmartButtonIcon, isCustomIcon: true },
			{
				componentType: ComponentType.ACTION,
				icon: WhiteboardIcon,
			},
			{ componentType: ComponentType.ACTION, icon: EmailIcon },
		],
	},
];

export const createFolderTemplateConfig = {
	id: TemplateId.createFolderAndPublishNewPage,
	description: i18n.createFolderAndPublishNewPageDescription,
	icons: [
		{ componentType: ComponentType.TRIGGER, icon: SmartButtonIcon, isCustomIcon: true },
		{ componentType: ComponentType.ACTION, icon: FolderIcon },
		{ componentType: ComponentType.ACTION, icon: PageIcon },
	],
};

export const createJiraIssueTemplateConfig = {
	id: TemplateId.publishNewPageAddLabelCreateJiraIssue,
	description: i18n.publishNewPageAddLabelCreateJiraIssueDescription,
	icons: [
		{ componentType: ComponentType.TRIGGER, icon: SmartButtonIcon, isCustomIcon: true },
		{ componentType: ComponentType.ACTION, icon: PageIcon },
		{ componentType: ComponentType.ACTION, icon: LabelIcon },
		{ componentType: ComponentType.ACTION, icon: JiraLogoIcon },
	],
};

export const invokeAgentAndPublishNewPageTemplateConfig = {
	id: TemplateId.invokeAgentAndPublishNewPage,
	description: i18n.invokeAgentAndPublishNewPageDescription,
	icons: [
		{ componentType: ComponentType.TRIGGER, icon: SmartButtonIcon, isCustomIcon: true },
		{ componentType: ComponentType.ACTION, icon: RovoLogoIcon },
		{ componentType: ComponentType.ACTION, icon: PageIcon },
	],
};

export const invokeAgentAndSendEmailTemplateConfig = {
	id: TemplateId.invokeAgentAndSendEmail,
	description: i18n.invokeAgentAndSendEmailDescription,
	icons: [
		{ componentType: ComponentType.TRIGGER, icon: SmartButtonIcon, isCustomIcon: true },
		{ componentType: ComponentType.ACTION, icon: RovoLogoIcon },
		{ componentType: ComponentType.ACTION, icon: EmailIcon },
	],
};

export const invokeAgentAndAddCommentTemplateConfig = {
	id: TemplateId.invokeAgentAndAddComment,
	description: i18n.invokeAgentAndAddCommentDescription,
	icons: [
		{ componentType: ComponentType.TRIGGER, icon: SmartButtonIcon, isCustomIcon: true },
		{ componentType: ComponentType.ACTION, icon: RovoLogoIcon },
		{ componentType: ComponentType.ACTION, icon: CommentIcon },
	],
};
